import type { UnitCode } from '~/graphql/graphql';

export type Dimensions = {
  lengthInches: number;
  widthInches: number;
  thicknessInches: number;
};

// Exact conversion: 1 inch³ = 0.000016387064 m³
const CUBIC_INCH_TO_CUBIC_METER = 0.000016387064;

/**
 * Units of the given UOM contained in one piece of lumber with the given dimensions.
 * Mirrors UnitUtil.unitsPerPieceRaw in the backend.
 */
export function unitsPerPiece(
  unitCode: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  switch (unitCode) {
    case 'LF':
      if (lengthInches <= 0)
        throw new Error(`Invalid lengthInches for LF: ${lengthInches}`);
      return lengthInches / 12;
    case 'MSF':
      if (lengthInches <= 0 || widthInches <= 0)
        throw new Error(
          `Invalid dimensions for MSF: length=${lengthInches}, width=${widthInches}`
        );
      return (lengthInches * widthInches) / 144_000;
    case 'SF':
      if (lengthInches <= 0 || widthInches <= 0)
        throw new Error(
          `Invalid dimensions for SF: length=${lengthInches}, width=${widthInches}`
        );
      return (lengthInches * widthInches) / 144;
    case 'BF':
      if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0)
        throw new Error(
          `Invalid dimensions for BF: length=${lengthInches}, width=${widthInches}, thickness=${thicknessInches}`
        );
      return (lengthInches * widthInches * thicknessInches) / 144;
    case 'MBF':
      if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0)
        throw new Error(
          `Invalid dimensions for MBF: length=${lengthInches}, width=${widthInches}, thickness=${thicknessInches}`
        );
      return (lengthInches * widthInches * thicknessInches) / 144_000;
    case 'CBM':
      if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0)
        throw new Error(
          `Invalid dimensions for CBM: length=${lengthInches}, width=${widthInches}, thickness=${thicknessInches}`
        );
      return (
        lengthInches * widthInches * thicknessInches * CUBIC_INCH_TO_CUBIC_METER
      );
    case 'PCS':
      return 1;
    case 'TALLIES':
      throw new Error('TALLIES is not a product unit');
    default:
      throw new Error(`Unsupported UnitCode: ${unitCode satisfies never}`);
  }
}

/**
 * Converts a quantity in pieces (PCS) to the given unit.
 * Mirrors the PCS → toUom path of UnitUtil.convertUnitsRaw in the backend.
 *
 * Result is rounded to 3 decimal places, matching backend SCALE_DEFAULT.
 */
export function convertPiecesToUnit(
  pieces: number,
  unitCode: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  if (pieces === 0) return 0;
  const perPiece = unitsPerPiece(
    unitCode,
    lengthInches,
    widthInches,
    thicknessInches
  );
  return Math.round(pieces * perPiece * 1000) / 1000;
}

export function toStandardUnits(
  quantity: number,
  fromUnit: UnitCode,
  toUnit: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  if (fromUnit === toUnit) return quantity;
  const pieces =
    quantity /
    unitsPerPiece(fromUnit, lengthInches, widthInches, thicknessInches);
  return convertPiecesToUnit(
    pieces,
    toUnit,
    lengthInches,
    widthInches,
    thicknessInches
  );
}

export function unitsPerPieceOrNull(
  unit: UnitCode,
  dims: Dimensions
): number | null {
  try {
    const upp = unitsPerPiece(
      unit,
      dims.lengthInches,
      dims.widthInches,
      dims.thicknessInches
    );
    return upp > 0 ? upp : null;
  } catch {
    return null;
  }
}

/**
 * Returns the stock quantity converted from pieces to the product's configured unit.
 */
export function getStockForUnit(
  stock: number,
  unitCode: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  return convertPiecesToUnit(
    stock,
    unitCode,
    lengthInches,
    widthInches,
    thicknessInches
  );
}

export function hasDimensions({
  lengthInches,
  widthInches,
  thicknessInches,
}: Dimensions): boolean {
  return lengthInches > 0 && widthInches > 0 && thicknessInches > 0;
}

/**
 * A price per `displayUnit` restated per `standardUnit`; null when it cannot be converted.
 * Does not round, matching the backend's `convertPriceRaw`.
 */
export function toStoragePriceOrNull(
  displayPrice: number,
  displayUnit: UnitCode,
  standardUnit: UnitCode | null | undefined,
  dims: Dimensions
): number | null {
  if (!standardUnit || displayUnit === standardUnit) return displayPrice;
  if (!hasDimensions(dims)) return null;
  const { lengthInches, widthInches, thicknessInches } = dims;
  try {
    return (
      (displayPrice *
        unitsPerPiece(
          displayUnit,
          lengthInches,
          widthInches,
          thicknessInches
        )) /
      unitsPerPiece(standardUnit, lengthInches, widthInches, thicknessInches)
    );
  } catch {
    return null;
  }
}

/**
 * Restates a price quoted per `pricedUnit` as a price per `targetUnit`, by
 * scaling it by how much of `pricedUnit` makes up one `targetUnit`.
 */
export function convertPricePerUnit(
  amount: number | null,
  targetUnit: UnitCode,
  pricedUnit: UnitCode,
  dims: Dimensions
): number | null {
  if (amount == null) return null;
  // Unconvertible prices stay as quoted: this runs during render, so throwing
  // would blank the cart.
  return toStoragePriceOrNull(amount, pricedUnit, targetUnit, dims) ?? amount;
}

/** The unit a price reads in after `convertPricePerUnit`; convertibility depends on units and dimensions, not the amount. */
export function pricedUnitFor(
  targetUnit: UnitCode,
  pricedUnit: UnitCode,
  dims: Dimensions
): UnitCode {
  return toStoragePriceOrNull(1, pricedUnit, targetUnit, dims) == null
    ? pricedUnit
    : targetUnit;
}
